import { Injectable, EventEmitter } from '@angular/core';

import { User } from '../models/user.model';
import { Utils } from '../utils/utils';
import { Slot } from '../models/schedule/slot.model';
import { Location } from '../models/location.model';
import { Appointment } from '../models/appointment.model';
import { ServiceMapper } from '../utils/service-mapper';

@Injectable()
/**
 * This class is used to keep track of the users progress through the appointment schedulig process
 * @param {User} user this is the current user
 * @param {Location} location this is the location the user selected
 * @param {Date} appointmentDate this is the date the user is scheduling an appointment for
 * @param {Date} appointmentTime this is the time the user is scheduling the appointment for
 * @param {Appointment} appointment this is the appointment object for this session
 * @emits {boolean} proceedToNextStep emits true when the appointment location, date, and time are set
 * @emits {boolean} sessionInitialized emits true after the session has been initialized
 */
export class SessionService {

    public proceedToNextStep: EventEmitter<boolean> = new EventEmitter<boolean>();

    public sessionInitialized: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isSessionInitialized = false;

    private _user: User = new User();
    public get user(): User {
        return this._user;
    } public set user(value: User) {
        this._user = value;
        if (this._appointment) {
            this._appointment.user = value;
        }
    }

    private _location: Location = new Location();
    public get location(): Location {
        return this._location;
    } public set location(value: Location) {
        this._location = value;
        this._appointment.location = value;
        if (this._service) {
            this.location.services.some(service => {
                if (service.name.toLocaleLowerCase() === this._service.toLocaleLowerCase()) {
                    this._appointment.service = service;
                    return true;
                }
                return false;
            });
        }
        this.proceedToNextStep.emit(true);
    }

    private _appointmentDate: Date;
    public get appointmentDate(): Date {
        return this._appointmentDate;
    } public set appointmentDate(value: Date) {
        this._appointmentDate = value;
        this._appointment.startDate = value.toISOString();
        this.proceedToNextStep.emit(true);
    }

    private _appointmentTime: Date;
    public get appointmentTime(): Date {
        return this._appointmentTime;
    } public set appointmentTime(value: Date) {
        this._appointmentTime = value;
        // the appointment time might be on a different day so lets set that too
        this._appointment.startTime = this._appointmentTime.toISOString();
        this.appointmentDate = value;
    }

    private _appointmentSlot: Slot;
    public get appointmentSlot(): Slot {
        return this._appointmentSlot;
    } public set appointmentSlot(value: Slot) {
        this._appointmentSlot = value;
    }

    private _appointment: Appointment = new Appointment();
    public get appointment(): Appointment {
        return this._appointment;
    } public set appointment(value: Appointment) {
        this._appointment = value;
    }

    private _service: string;
    public get service(): string {
        return this._service;
    } public set service(value: string) {
        this._service = ServiceMapper.getNameFromCode(value);
    }

    private _returnUrl: string;
    public get returnUrl(): string {
        return this._returnUrl;
    } public set returnUrl(value: string) {
        this._returnUrl = value;
    }

    /**
     * @prop {number} locationID this id is set when the user has come from an internal scheduler with a specific location to look up
     */
    private _locationID: number;
    public get locationID(): number {
        return this._locationID;
    } public set locationID(value: number) {
        this._locationID = value;
    }

    /**
     * @prop {string} token this is the auth token for service calls
     */
    private _token: string;
    public get token(): string {
        return this._token;
    } public set token(value: string) {
        this._token = value;
    }

    /**
     * @prop {string} appointmentType string representation of the appointment type new || reschedule
     */
    private _appointmentType: string;
    public get appointmentType(): string {
        return this._appointmentType;
    } public set appointmentType(value: string) {
        this._appointmentType = value;
    }

    private _finalizeAppointmentExternally:boolean = false;
    public get finalizeAppointmentExternally():boolean {
        return this._finalizeAppointmentExternally;
    }
    public set finalizeAppointmentExternally(value:boolean) {
        this._finalizeAppointmentExternally = value;
        // //console.log('this._finalizeAppointmentExternally', this._finalizeAppointmentExternally);
    }

    /**
     * @prop {boolean} viewOnly Are we in view only mode or are we actually scheduling an appointment
     */
    private _viewOnly: boolean = false;
    public get viewOnly(): boolean {
        return this._viewOnly;
    } public set viewOnly(value: boolean) {
        this._viewOnly = value;
    }

    private _requireReason:boolean = false;
    public get requireReason():boolean {
        return this._requireReason;
    }
    public set requireReason(value:boolean) {
        this._requireReason = value;
    }

    private _reason: string;
    public get reason():string {
        return this._reason;
    }
    public set reason(value:string) {
        this._reason = value;
    }

    /**
     * @prop {number} numberOfAppointments the number of minimum appointments to filter the calandar view on
     */
    private _numberOfAppointmetns: number = null;
    public get numberOfAppointments(): number {
        return this._numberOfAppointmetns;
    } public set numberOfAppointments(value: number) {
        this._numberOfAppointmetns = value;
    }

    /**
     * @prop {boolean} filterSoonest
     */
    private _filterSoonest: boolean = true;
    public get filterSoonest(): boolean {
        return this._filterSoonest;
    } public set filterSoonest(value: boolean) {
        this._filterSoonest = value;
    }

    /**
     * @prop {string} filterDate
     */
    private _filterDate: string = null;
    public get filterDate(): string {
        return this._filterDate;
    } public set filterDate(value: string) {
        this._filterDate = value;
    }

    private _effectiveDate: boolean = false;
    public get effectiveDate(): boolean {
        return this._effectiveDate;
    } public set effectiveDate(value: boolean) {
        this._effectiveDate = value;
    }

    private _lang: string = undefined;
    public get lang(): string {
        return this._lang;
    } public set lang(value: string) {
        this._lang = value;
    }

    // The variable name probably should be is schedulingRemote as it is being used currently
    private _remoteAllowed: boolean = false;
    public get remoteAllowed(): boolean {
        return this._remoteAllowed;
    } public set remoteAllowed(value: boolean) {
        this._remoteAllowed = value;
    }

    private _authenticatedViaCookie: boolean = false;
    public get authenticatedViaCookie(): boolean {
        return this._authenticatedViaCookie;
    }
    public set authenticatedViaCookie(value: boolean) {
        this._authenticatedViaCookie = value;
    }


    // Helper to check if the user was logged in with cookie or token
    public isUserLoggedIn(): boolean {
        if (this._authenticatedViaCookie || (this._token && this._token.length > 0)) {
            return true;
        } else {
            return false;
        }
    }
    constructor() {
        // //console.log('session constructor');
    }

}
